import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCardContext } from '../../../context/card-context/card-context';
import { RotateControls } from '../../../features/card-editor/components/rotate-controls/rotate-controls';
import { usePhotoControls } from '../../../features/card-editor/hooks/use-photo-controls';
import { isPhotozoneImage } from '../../../features/card-editor/utils/is-photozone-image';
import { CustomFabricObject } from '../../../global-types/canvas';
import { useActiveCanvas, useFeatureFlags } from '../../../hooks';
import { Drawer } from '../drawer/drawer';
import { DrawerPositions } from '../drawer/drawer-types';
import { HeaderContent, FooterContent } from './fragments';
import { RotateDrawerProps } from './rotate-drawer-types';
import { ensureImageFillsPhotoZone, getComputedRotation } from './rotate-drawer-utils';
import styles from './rotate-drawer.module.scss';
import { CUIText } from '@dotcom/crown-ui';

/**
 * Drawer component for rotating selected photo objects on the card canvas.
 * Handles rotation logic, state restoration on cancel, and ensures image fills photo zone after rotation.
 *
 * @param isOpen - Whether the drawer is open
 * @param onDrawerCancel - Callback for cancel action
 * @param onDrawerApply - Callback for apply action
 * @param onChangeCallback - Optional callback for when rotation changes
 * @returns React.ReactElement
 */
export const RotateDrawer = ({
  isOpen,
  onDrawerCancel,
  onDrawerApply,
  onChangeCallback,
}: RotateDrawerProps): React.ReactElement => {
  const { t } = useTranslation();
  const canvas = useActiveCanvas();
  const { IS_FORCE_FILL_ENABLED } = useFeatureFlags();
  const { rotateSelectedPhoto } = usePhotoControls();
  const { cardState } = useCardContext();
  const { cardFacesList, activeCardIndex } = cardState;
  const currentCardFace = cardFacesList[`${activeCardIndex}`];
  const isFrontFace = currentCardFace.type === 'front' && currentCardFace.active === true;
  const rotateValue = isFrontFace ? 90 : 45;
  const activeObject = canvas?.current?.getActiveObject();
  const originalObjectRef = useRef<CustomFabricObject | null>(null);
  const currentAngle = activeObject?.angle ?? 0;
  /**
   * Restores the original state of the active object and triggers the cancel callback.
   */
  const handleCancel = () => {
    if (activeObject && originalObjectRef.current) {
      activeObject.set('scaleX', originalObjectRef.current.scaleX);
      activeObject.set('scaleY', originalObjectRef.current.scaleY);
      rotateSelectedPhoto(getComputedRotation(originalObjectRef.current.angle || 0));
      activeObject.left = originalObjectRef.current.left;
      activeObject.top = originalObjectRef.current.top;
    }
    onDrawerCancel();
  };

  /**
   * Rotates the selected photo left or right, ensures it fills the photo zone, and triggers change callback.
   *
   * @param rotateRight - If true, rotates right; otherwise, rotates left.
   */
  const onRotateClick = (rotateRight: boolean) => {
    rotateSelectedPhoto(
      Number(
        rotateRight
          ? getComputedRotation((activeObject?.angle || 0) + rotateValue)
          : getComputedRotation((activeObject?.angle || 0) - rotateValue),
      ),
    );

    if (activeObject && isPhotozoneImage(activeObject)) {
      const photozoneId = activeObject?.data?.photoZoneId;
      const photozoneObject = canvas?.current?.getObjects().find((obj) => obj.name === photozoneId);

      if (photozoneObject) {
        ensureImageFillsPhotoZone(activeObject, photozoneObject);
        const photoZoneCenter = photozoneObject.getPointByOrigin('center', 'center');
        activeObject.setPositionByOrigin(photoZoneCenter, 'center', 'center');
        activeObject.setCoords();
      }
      canvas?.current?.renderAll();
    }

    if (onChangeCallback) {
      onChangeCallback();
    }
  };

  /**
   * Stores the original state of the active object when the drawer is opened.
   */
  useEffect(() => {
    if (isOpen && activeObject) {
      originalObjectRef.current = activeObject.toObject();
    }
  }, [isOpen, activeObject]);

  return (
    <Drawer
      drawerPosition={DrawerPositions.Bottom}
      hideCloseButton={true}
      onClose={handleCancel}
      isOpen={isOpen}
      addClass={styles['rotate-drawer']}
      headerContent={
        IS_FORCE_FILL_ENABLED ? (
          <RotateControls onRotateLeft={() => onRotateClick(false)} onRotateRight={() => onRotateClick(true)} />
        ) : (
          <HeaderContent onChangeCallback={onChangeCallback} initialValue={currentAngle} />
        )
      }
      role="toolbar"
      showVeil={true}
      footerContent={<FooterContent onDrawerCancel={handleCancel} onDrawerApply={onDrawerApply} />}
    >
      <CUIText as="span" variant="screen-reader">
        {t('rotateDrawer.rotateDrawer')}
      </CUIText>
    </Drawer>
  );
};

RotateDrawer.defaultProps = {
  addClass: '',
};
